import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import LoadingSpinner from "../components/LoadingSpinner";
import {
  completeCheckOut,
  createCheckoutPaymentLink,
  fetchCheckOutFlow,
  redirectToPaymentLink,
} from "../services/reservationService";
import { useAppLanguage } from "../hooks/useAppLanguage";
import { canUseCapability } from "../utils/journeyAccess";
import usePaymentVerification, { isVerifiedZeroPayment } from "../hooks/usePaymentVerification";
import {
  clearPaymentResume,
  PAYMENT_FLOW_DEPARTURE,
  readPaymentResume,
  savePaymentResume,
} from "../utils/paymentResume";

const CHECKOUT_DRY_RUN = true;
const CHECKOUT_ACTION = "CHECKOUT_ONLY";

const checkoutTranslations = {
  de: {
    language: "Sprache",
    checkout: "Checkout",
    unavailableTitle: "Checkout nicht verfügbar",
    ownerOnlyTitle: "Nur für Buchungsinhaber",
    ownerOnlyCopy: "Dieser Link ist ein Zimmergast-Link. Checkout und Zahlung sind nur über den Buchungsinhaber-Link verfügbar.",
    onlineTitle: "Online Checkout",
    doneTitle: "Fertig",
    loading: "Checkout wird vorbereitet...",
    reservationNotFound: "Reservierung nicht gefunden.",
    genericError: "Das hat leider nicht geklappt. Bitte versuchen Sie es erneut.",
    checkDetails: "Bitte prüfen Sie die Angaben und versuchen Sie es erneut.",
    validationUnexpected: "Die Checkout-Prüfung hat eine unerwartete Antwort erhalten. Bitte versuchen Sie es erneut.",
    validatedNotCompleted: "Der Checkout wurde geprüft, aber nicht abgeschlossen. Bitte versuchen Sie es erneut.",
    checkoutAfterPayment: "Checkout ist verfügbar, sobald die Zahlung abgeschlossen ist.",
    openBalance: "Offener Betrag: {amount}. Checkout ist verfügbar, sobald die Zahlung abgeschlossen ist.",
    notSettled: "nicht beglichen",
    paymentRequired: "Zahlung erforderlich",
    openBalanceShort: "Offener Betrag",
    paymentDisabled: "Checkout-Aktionen sind deaktiviert, bis der offene Betrag beglichen ist.",
    preparingPayment: "Zahlung wird vorbereitet...",
    verifyingPayment: "Zahlung wird mit dem Hotelsystem abgeglichen...",
    paymentVerificationPending: "Die Zahlung wurde noch nicht bestätigt. Wir prüfen weiter.",
    paymentVerificationUnavailable: "Die Zahlungsprüfung ist vorübergehend nicht verfügbar. Wir prüfen weiter.",
    paymentVerificationTimeout: "Die Zahlungsbestätigung dauert länger als erwartet.",
    paymentVerificationStopped: "Die Zahlungsprüfung konnte nicht fortgesetzt werden. Bitte öffnen Sie Ihren Gastlink erneut oder kontaktieren Sie das Hotel.",
    checkPaymentAgain: "Zahlungsstatus erneut prüfen",
    payBalance: "Offenen Betrag bezahlen",
    paymentLinkUnavailable: "Der Zahlungslink ist noch nicht verfügbar. Bitte kontaktieren Sie den Support.",
    existingPaymentLink: "Bitte bitten Sie das Team, den bestehenden ausstehenden Zahlungslink zu verwenden oder zu stornieren.",
    back: "Zurück",
    backHome: "Zurück zur Startseite",
    backTrip: "Zurück zur Reise",
    checkingOut: "Checkout läuft...",
    confirmCheckout: "Checkout bestätigen",
    confirmCheckoutCopy: "Bitte bestätigen Sie den Checkout. Ihre bereits gespeicherten Registrierungsdaten werden verwendet.",
    accessWarning: "Wichtig: Wenn Sie jetzt auschecken, wird Ihr Türzugang sofort deaktiviert. Sie können danach weder Ihr Zimmer noch andere Hoteltüren oder die Tiefgarageneinfahrt öffnen.",
    checkoutSuccess: "Check-out erfolgreich.",
  },
  en: {
    language: "Language",
    checkout: "Checkout",
    unavailableTitle: "Checkout unavailable",
    ownerOnlyTitle: "Booking owner only",
    ownerOnlyCopy: "This is a room guest link. Checkout and payment are only available from the booking owner link.",
    onlineTitle: "Online Checkout",
    doneTitle: "Done",
    loading: "Preparing checkout...",
    reservationNotFound: "Reservation not found.",
    genericError: "Something went wrong. Please try again.",
    checkDetails: "Please check the details and try again.",
    validationUnexpected: "Checkout validation returned an unexpected response. Please try again.",
    validatedNotCompleted: "Checkout was validated but not completed. Please try again.",
    checkoutAfterPayment: "Checkout is available after payment is settled.",
    openBalance: "Open balance: {amount}. Checkout is available after payment is settled.",
    notSettled: "not settled",
    paymentRequired: "Payment required",
    openBalanceShort: "Open balance",
    paymentDisabled: "Checkout actions are disabled until the balance is settled.",
    preparingPayment: "Preparing payment...",
    verifyingPayment: "Verifying your payment with the hotel system...",
    paymentVerificationPending: "Payment has not been confirmed yet. We will keep checking.",
    paymentVerificationUnavailable: "Payment verification is temporarily unavailable. We will keep checking.",
    paymentVerificationTimeout: "Payment confirmation is taking longer than expected.",
    paymentVerificationStopped: "Payment verification could not continue. Please reopen your guest link or contact the hotel.",
    checkPaymentAgain: "Check payment status again",
    payBalance: "Pay balance",
    paymentLinkUnavailable: "Payment link is not available yet. Please contact support.",
    existingPaymentLink: "Please ask staff to use or cancel the existing pending payment link.",
    back: "Back",
    backHome: "Back to home",
    backTrip: "Back to trip",
    checkingOut: "Checking out...",
    confirmCheckout: "Confirm checkout",
    confirmCheckoutCopy: "Please confirm checkout. Your saved registration details will be used.",
    accessWarning: "Important: If you check out now, your door access will be disabled immediately. You will no longer be able to enter your room or open any hotel door, including the parking garage entrance.",
    checkoutSuccess: "Check-out successful.",
  },
};

const shouldRedirectToApp = (checkout) =>
  Boolean(checkout?.should_redirect_to_app);

const isLiveCompletionApplied = (result) =>
  result?.dry_run === false && result?.completion_applied === true;

const getNumericMoneyAmount = (value) => {
  if (value === undefined || value === null || value === "") return null;
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const normalized = value.replace(/[^\d,.-]/g, "").replace(",", ".");
    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }
  if (typeof value === "object") {
    return getNumericMoneyAmount(
      value.amount ??
      value.value ??
      value.grossAmount ??
      value.netAmount ??
      value.totalAmount ??
      value.balance
    );
  }
  return null;
};

const hasPositiveMoneyAmount = (value) => {
  const amount = getNumericMoneyAmount(value);
  return amount !== null && amount > 0;
};

const formatBalance = (balance) => {
  if (!balance) return "";
  if (typeof balance === "string" || typeof balance === "number") return String(balance);
  const amount = balance.amount ?? balance.value ?? balance.grossAmount;
  const currency = balance.currency || balance.currencyCode || "EUR";
  return amount === undefined || amount === null ? "" : `${amount} ${currency}`;
};

const interpolate = (template, values = {}) =>
  Object.entries(values).reduce(
    (result, [key, value]) => result.replaceAll(`{${key}}`, value ?? ""),
    template
  );

const getErrorMessage = (error, t = checkoutTranslations.en) => {
  const data = error?.response?.data;
  if (!data) return t.genericError;
  if (typeof data === "string") return cleanApiErrorMessage(data) || data;
  const checkoutMessage = formatBlockers(data.errors?.checkout);
  if (checkoutMessage) {
    return checkoutMessage;
  }
  return cleanApiErrorMessage(data.detail || data.error || data.message) || t.checkDetails;
};

const cleanApiErrorMessage = (message) => {
  if (!message || typeof message !== "string") {
    return "";
  }

  const jsonStart = message.indexOf("{");
  if (jsonStart !== -1) {
    try {
      const parsed = JSON.parse(message.slice(jsonStart));
      if (Array.isArray(parsed.messages) && parsed.messages.length > 0) {
        return parsed.messages.join(" ");
      }
      if (parsed.message) {
        return parsed.message;
      }
      if (parsed.error) {
        return parsed.error;
      }
    } catch {
      // Fall through to prefix cleanup.
    }
  }

  return makeGuestFacingError(message.replace(/^Apaleo API error \d+:\s*/i, "").trim());
};

const makeGuestFacingError = (message) => {
  if (!message || typeof message !== "string") {
    return "";
  }

  return message
    .replace(/^Apaleo does not allow online checkout yet/i, "Online checkout is not available yet")
    .replace(/^Apaleo does not allow checkout yet/i, "Checkout is not available yet")
    .replace(/\bApaleo\b/g, "the booking system")
    .trim();
};

const formatBlockers = (blockers) => {
  if (!blockers) return "";
  if (typeof blockers === "string") return cleanApiErrorMessage(blockers) || blockers;
  if (Array.isArray(blockers)) {
    return blockers
      .map((blocker) => {
        if (typeof blocker === "string") return cleanApiErrorMessage(blocker) || blocker;
        return makeGuestFacingError(blocker.message || blocker.detail || blocker.error || JSON.stringify(blocker));
      })
      .filter(Boolean)
      .join(" ");
  }
  if (typeof blockers === "object") {
    return makeGuestFacingError(blockers.message || blockers.detail || blockers.error || Object.values(blockers).flat().join(" "));
  }
  return makeGuestFacingError(String(blockers));
};

const getCheckoutBlockerMessage = (error) => {
  const data = error?.response?.data;
  const checkoutErrors = data?.errors?.checkout || data?.checkout?.errors;
  return formatBlockers(checkoutErrors);
};

const getPaymentErrorMessage = (error) => {
  const data = error?.response?.data;
  const paymentErrors = data?.errors?.payment || data?.checkout?.payment?.errors || data?.payment;
  return formatBlockers(paymentErrors) || getErrorMessage(error);
};

const CheckoutPage = () => {
  const navigate = useNavigate();
  const reservationCode = localStorage.getItem("reservation-code");
  const initialPaymentResumeRef = useRef(readPaymentResume(reservationCode));
  const [language, setLanguage] = useAppLanguage();
  const t = checkoutTranslations[language] || checkoutTranslations.de;
  const checkoutCapabilityAllowed = canUseCapability("checkout");
  const paymentCapabilityAllowed = canUseCapability("payment");
  const ownerCheckoutAllowed = checkoutCapabilityAllowed || paymentCapabilityAllowed;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [flowData, setFlowData] = useState(null);
  const [step, setStep] = useState("confirm");
  const [globalError, setGlobalError] = useState("");
  const [checkoutBlocker, setCheckoutBlocker] = useState("");
  const [paymentError, setPaymentError] = useState("");
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentLinkCreationBlocked, setPaymentLinkCreationBlocked] = useState(false);
  const [paymentFlowStarted, setPaymentFlowStarted] = useState(
    initialPaymentResumeRef.current?.flow === PAYMENT_FLOW_DEPARTURE && initialPaymentResumeRef.current?.pending === true
  );
  const selectedFolio = flowData?.selected_folio || flowData?.folios?.[0] || {};
  const eligibility = flowData?.checkout?.eligibility || {};
  const paymentState = flowData?.checkout?.payment || {};
  const folioBalance = selectedFolio.balance || selectedFolio.open_balance || selectedFolio.outstanding_balance;
  const hasOpenBalance = Boolean(
    eligibility.has_open_balance ||
    paymentState.required ||
    hasPositiveMoneyAmount(paymentState.amount) ||
    hasPositiveMoneyAmount(eligibility.balance) ||
    hasPositiveMoneyAmount(folioBalance)
  );
  const openBalanceValue =
    paymentState.amount !== undefined
      ? { amount: paymentState.amount, currency: paymentState.currency }
      : eligibility.balance || folioBalance;
  const openBalanceAmount =
    formatBalance(openBalanceValue);
  const openBalanceMessage =
    hasOpenBalance
      ? interpolate(t.openBalance, { amount: openBalanceAmount || t.notSettled })
      : "";
  const canRequestPaymentLink = paymentCapabilityAllowed && Boolean(paymentState.can_request_payment_link || hasOpenBalance);
  const paymentLinkUrl = paymentState.payment_link_url || "";
  const canCheckoutOnly =
    checkoutCapabilityAllowed &&
    !hasOpenBalance &&
    eligibility.can_checkout_only !== false;

  const redirectToApp = useCallback(() => {
    navigate("/home", { replace: true });
  }, [navigate]);

  useEffect(() => {
    let isMounted = true;

    if (!ownerCheckoutAllowed) {
      setGlobalError(t.ownerOnlyCopy);
      setLoading(false);
      return;
    }

    if (!reservationCode) {
      setGlobalError(t.reservationNotFound);
      setLoading(false);
      return;
    }

    fetchCheckOutFlow(reservationCode)
      .then((data) => {
        if (!isMounted) return;
        if (data.checkout?.is_complete) {
          setFlowData(data);
          setStep("complete");
          return;
        }
        if (shouldRedirectToApp(data.checkout)) {
          redirectToApp();
          return;
        }

        const paymentResume = readPaymentResume(reservationCode);
        setFlowData(data);
        if (paymentResume?.flow === PAYMENT_FLOW_DEPARTURE && paymentResume.pending) {
          setStep("confirm");
          if (isVerifiedZeroPayment(data.checkout?.payment)) {
            clearPaymentResume(reservationCode);
            setPaymentFlowStarted(false);
          } else {
            setPaymentFlowStarted(true);
          }
        }
      })
      .catch((error) => {
        console.error("Failed to load checkout flow", error);
        setGlobalError(getErrorMessage(error, t));
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [ownerCheckoutAllowed, redirectToApp, reservationCode, t]);

  const verifyCheckoutPayment = useCallback(() => fetchCheckOutFlow(reservationCode), [reservationCode]);
  const selectCheckoutPayment = useCallback((data) => data?.checkout?.payment || {}, []);
  const handleCheckoutPaymentResponse = useCallback((data) => {
    setFlowData(data);
  }, []);
  const handleCheckoutPaymentSettled = useCallback(() => {
    setPaymentFlowStarted(false);
    clearPaymentResume(reservationCode);
  }, [reservationCode]);
  const persistCheckoutPaymentProgress = useCallback(({ startedAt, attempts }) => {
    savePaymentResume(reservationCode, {
      flow: PAYMENT_FLOW_DEPARTURE,
      step: "confirm",
      selectedAction: CHECKOUT_ACTION,
      pending: true,
      settled: false,
      startedAt,
      attempts,
    });
  }, [reservationCode]);
  const paymentVerification = usePaymentVerification({
    active: paymentFlowStarted && !loading,
    scopeKey: reservationCode,
    verify: verifyCheckoutPayment,
    selectPayment: selectCheckoutPayment,
    onResponse: handleCheckoutPaymentResponse,
    onSettled: handleCheckoutPaymentSettled,
    onProgress: persistCheckoutPaymentProgress,
    initialStartedAt: initialPaymentResumeRef.current?.startedAt,
    initialAttempts: initialPaymentResumeRef.current?.attempts,
    startDelayMs: 5000,
  });

  const complete = async () => {
    setSaving(true);
    setGlobalError("");
    setCheckoutBlocker("");
    try {
      if (!canCheckoutOnly) {
        setCheckoutBlocker(openBalanceMessage || t.checkoutAfterPayment);
        return;
      }

      const dryRunResult = await completeCheckOut(reservationCode, CHECKOUT_ACTION, CHECKOUT_DRY_RUN);
      if (dryRunResult?.dry_run !== true && dryRunResult?.completion_applied !== false) {
        setGlobalError(t.validationUnexpected);
        return;
      }

      const result = await completeCheckOut(reservationCode, CHECKOUT_ACTION, false);
      if (!isLiveCompletionApplied(result)) {
        setGlobalError(t.validatedNotCompleted);
        return;
      }

      if (shouldRedirectToApp(result.checkout)) {
        redirectToApp();
        return;
      }

      setStep("complete");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      console.error("Failed to complete checkout", error);
      if (error?.response?.status === 409) {
        redirectToApp();
        return;
      }
      if (error?.response?.status === 400) {
        const blockerMessage = getCheckoutBlockerMessage(error);
        if (blockerMessage) {
          setCheckoutBlocker(blockerMessage);
          return;
        }
      }
      setGlobalError(getErrorMessage(error, t));
    } finally {
      setSaving(false);
    }
  };

  const payBalance = async () => {
    if (paymentLinkCreationBlocked && !paymentLinkUrl) {
      return;
    }

    setPaymentLoading(true);
    setPaymentError("");
    setGlobalError("");
    setCheckoutBlocker("");

    try {
      let url = paymentLinkUrl;

      if (!url) {
        const result = await createCheckoutPaymentLink(reservationCode);
        url = result.payment_link_url || result.checkout?.payment?.payment_link_url || "";
        if (result.reused_existing_link && url) {
          setPaymentError("");
        }
        if (result.checkout) {
          setFlowData((current) => ({
            ...current,
            checkout: {
              ...current?.checkout,
              ...result.checkout,
            },
          }));
        }
        if (result.payment_required === false && !url) {
          const refreshed = await fetchCheckOutFlow(reservationCode);
          setFlowData(refreshed);
          if (isVerifiedZeroPayment(refreshed?.checkout?.payment)) {
            clearPaymentResume(reservationCode);
            setPaymentFlowStarted(false);
            return;
          }
        }
      }

      if (!url) {
        setPaymentError(t.paymentLinkUnavailable);
        return;
      }

      initialPaymentResumeRef.current = savePaymentResume(reservationCode, {
        flow: PAYMENT_FLOW_DEPARTURE,
        step: "confirm",
        selectedAction: CHECKOUT_ACTION,
        pending: true,
        settled: false,
        startedAt: 0,
        attempts: 0,
      });
      setPaymentFlowStarted(true);
      redirectToPaymentLink(url);
    } catch (error) {
      console.error("Failed to create payment link", error);
      const existingUrl = error?.response?.data?.checkout?.payment?.payment_link_url;
      if (existingUrl) {
        setFlowData((current) => ({
          ...current,
          checkout: {
            ...current?.checkout,
            payment: {
              ...current?.checkout?.payment,
              payment_link_url: existingUrl,
            },
          },
        }));
        initialPaymentResumeRef.current = savePaymentResume(reservationCode, {
          flow: PAYMENT_FLOW_DEPARTURE,
          step: "confirm",
          selectedAction: CHECKOUT_ACTION,
          pending: true,
          settled: false,
          startedAt: 0,
          attempts: 0,
        });
        setPaymentFlowStarted(true);
        redirectToPaymentLink(existingUrl);
        return;
      }

      const paymentMessage = getPaymentErrorMessage(error);
      if (error?.response?.status === 400 && error?.response?.data?.errors?.payment) {
        setPaymentLinkCreationBlocked(true);
      }
      setPaymentError(
        `${paymentMessage} ${t.existingPaymentLink}`
      );
    } finally {
      setPaymentLoading(false);
    }
  };

  if (loading) {
    return (
      <main className="checkout-app">
        <div className="checkout-loader">
          <LoadingSpinner />
          <p>{t.loading}</p>
        </div>
      </main>
    );
  }

  if (!ownerCheckoutAllowed) {
    return (
      <main className="checkout-app">
        <section className="checkout-section">
          <p className="checkout-kicker">{t.checkout}</p>
          <h1>{t.ownerOnlyTitle}</h1>
          <p className="checkout-copy">{t.ownerOnlyCopy}</p>
          <button className="checkout-button primary" type="button" onClick={() => navigate("/home")}>
            {t.backHome}
          </button>
        </section>
      </main>
    );
  }

  if (globalError && !flowData) {
    return (
      <main className="checkout-app">
        <section className="checkout-section">
          <p className="checkout-kicker">{t.checkout}</p>
          <h1>{t.unavailableTitle}</h1>
          {/* <p className="checkout-error-block">{globalError}</p> */}
          <button className="checkout-button primary" type="button" onClick={() => navigate("/home")}>
            {t.backHome}
          </button>
        </section>
      </main>
    );
  }

  return (
    <main className="checkout-app">
      <section className="checkout-section">
        <div className="page-header-top">
          <p className="checkout-kicker">{t.checkout}</p>
          <label className="home-language-switcher">
            <span className="visually-hidden">{t.language}</span>
            <select value={language} onChange={(event) => setLanguage(event.target.value)}>
              <option value="de">DE</option>
              <option value="en">EN</option>
            </select>
          </label>
        </div>
        <h1>{step === "complete" ? t.doneTitle : t.onlineTitle}</h1>
        {globalError && <p className="checkout-error-block">{globalError}</p>}
        {checkoutBlocker && <p className="checkout-error-block">{checkoutBlocker}</p>}
        {paymentError && <p className="checkout-error-block">{paymentError}</p>}
        {paymentFlowStarted && paymentVerification.status === "verifying" && <p className="checkout-copy" role="status">{t.verifyingPayment}</p>}
        {paymentFlowStarted && paymentVerification.status === "pending" && <p className="checkout-copy" role="status">{t.paymentVerificationPending}</p>}
        {paymentFlowStarted && paymentVerification.status === "unavailable" && <p className="checkout-error-block">{t.paymentVerificationUnavailable}</p>}
        {paymentFlowStarted && paymentVerification.status === "timed_out" && <p className="checkout-error-block">{t.paymentVerificationTimeout}</p>}
        {paymentFlowStarted && paymentVerification.status === "error" && <p className="checkout-error-block">{t.paymentVerificationStopped}</p>}
        {paymentFlowStarted && ["timed_out", "error"].includes(paymentVerification.status) && (
          <button className="checkout-button secondary" type="button" onClick={paymentVerification.restart}>
            {t.checkPaymentAgain}
          </button>
        )}
        {openBalanceMessage && step !== "complete" && (
          <div className="checkout-balance-block">
            <span>{t.paymentRequired}</span>
            <strong>{openBalanceAmount || t.openBalanceShort}</strong>
            <p>{t.paymentDisabled}</p>
            {!paymentFlowStarted && (canRequestPaymentLink || paymentLinkUrl) && (
              <button
                className="checkout-button primary"
                type="button"
                disabled={paymentLoading || (paymentLinkCreationBlocked && !paymentLinkUrl)}
                onClick={payBalance}
              >
                {paymentLoading ? t.preparingPayment : t.payBalance}
              </button>
            )}
          </div>
        )}

        {step === "confirm" && (
          <>
            <p className="checkout-copy">{t.confirmCheckoutCopy}</p>
            <p className="checkout-access-warning" role="note">{t.accessWarning}</p>
            <div className="checkout-actions">
              <button className="checkout-button secondary" type="button" onClick={() => navigate("/home")}>
                {t.back}
              </button>
              <button className="checkout-button primary" type="button" disabled={saving || !canCheckoutOnly} onClick={complete}>
                {saving ? t.checkingOut : t.confirmCheckout}
              </button>
            </div>
          </>
        )}

        {step === "complete" && (
          <CompletionScreen
            onHome={() => navigate("/home")}
            t={t}
          />
        )}
      </section>
    </main>
  );
};

const CompletionScreen = ({ onHome, t }) => (
  <div className="checkout-complete">
    <p className="checkout-checkmark">✓</p>
    <h2>{t.checkoutSuccess}</h2>
    <button className="checkout-button primary" type="button" onClick={onHome}>
      {t.backTrip}
    </button>
  </div>
);

export default CheckoutPage;
